import React, { useState } from 'react';
import { MapPin, X } from 'lucide-react';

const AddAddressModal = ({ isOpen, onClose, onSave, editAddress }) => {
  const [formData, setFormData] = useState(editAddress || {
    label: 'Home',
    recipient_name: '',
    landmark: '',
    address: '',
    city: 'Gurgaon',
    state: 'Haryana',
    country: 'India',
    pincode: '',
    phone: '',
    latitude: '',
    longitude: ''
  });

  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.label.trim()) newErrors.label = 'Label is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.pincode || formData.pincode.length !== 6) {
      newErrors.pincode = 'Valid 6-digit pincode required';
    }
    if (!formData.phone || formData.phone.length !== 10) {
      newErrors.phone = 'Valid 10-digit phone required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white w-full max-w-md rounded-2xl sm:rounded-3xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <MapPin className="text-blue-600" />
            {editAddress ? 'Edit Address' : 'Add New Address'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Label */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Address Label
            </label>
            <input
              type="text"
              name="label"
              value={formData.label}
              onChange={handleChange}
              placeholder="e.g., Home, Office, Other"
              className={`w-full border-2 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 outline-none transition-all ${
                errors.label ? 'border-red-300' : 'border-gray-200 focus:border-blue-500'
              }`}
            />
            {errors.label && <p className="text-red-600 text-xs mt-1">{errors.label}</p>}
          </div>

            {/* Recipient Name */}
            <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Recipient Name
            </label>
            <input
              type="text"
              name="recipient_name" 
                value={formData.recipient_name}
                onChange={handleChange}
                placeholder="Name of the recipient"
                className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 outline-none focus:border-blue-500 transition-all"
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Complete Address
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="House/Flat No., Street, Landmark"
              rows="3"
              className={`w-full border-2 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 outline-none transition-all resize-none ${
                errors.address ? 'border-red-300' : 'border-gray-200 focus:border-blue-500'
              }`}
            />
            {errors.address && <p className="text-red-600 text-xs mt-1">{errors.address}</p>}
          </div>
         {/* landmark */}
         <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Landmark
            </label>
            <input
              type="text"
              name="landmark"
              value={formData.landmark}
              onChange={handleChange}
              placeholder="Nearby landmark"
              className={`w-full border-2 rounded-xl px-3 py-2.5 outline-none transition-all ${
                errors.landmark ? 'border-red-300' : 'border-gray-200 focus:border-blue-500'
              }`}
            />
            {errors.landmark && <p className="text-red-600 text-xs mt-1">{errors.landmark}</p>}
          </div>

          {/* City & State */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                City
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="City"
                className={`w-full border-2 rounded-xl px-3 py-2.5 outline-none transition-all ${
                  errors.city ? 'border-red-300' : 'border-gray-200 focus:border-blue-500'
                }`}
              />
              {errors.city && <p className="text-red-600 text-xs mt-1">{errors.city}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                State
              </label>
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 outline-none focus:border-blue-500 transition-all"
              >
                <option value="Haryana">Haryana</option>
                <option value="Delhi">Delhi</option>
                <option value="Punjab">Punjab</option>
                <option value="Uttar Pradesh">Uttar Pradesh</option>
                <option value="Rajasthan">Rajasthan</option>
              </select>
            </div>
          </div>
          
          {/* Pincode & Phone */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Pincode
              </label>
              <input
                type="text"
                name="pincode"
                value={formData.pincode}
                onChange={(e) => {
                  if (/^\d{0,6}$/.test(e.target.value)) {
                    handleChange(e);
                  }
                }}
                placeholder="110001"
                maxLength="6"
                className={`w-full border-2 rounded-xl px-3 py-2.5 outline-none transition-all ${
                  errors.pincode ? 'border-red-300' : 'border-gray-200 focus:border-blue-500'
                }`}
              />
              {errors.pincode && <p className="text-red-600 text-xs mt-1">{errors.pincode}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Phone
              </label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={(e) => {
                  if (/^\d{0,10}$/.test(e.target.value)) {
                    handleChange(e);
                  }
                }}
                placeholder="9876543210"
                maxLength="10"
                className={`w-full border-2 rounded-xl px-3 py-2.5 outline-none transition-all ${
                  errors.phone ? 'border-red-300' : 'border-gray-200 focus:border-blue-500'
                }`}
              />
              {errors.phone && <p className="text-red-600 text-xs mt-1">{errors.phone}</p>}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all"
            >
              {editAddress ? 'Update' : 'Add'} Address
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAddressModal;